"use client";

import { useState, useRef, useCallback, useMemo } from "react";
import Link from "next/link";
import ResumeUpload from "@/components/career/ResumeUpload";

export default function JobClient({ job }) {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    linkedin: "",
    location: "",
    resume: null,
  });

  const lastSubmit = useRef(0);
  const SUBMIT_COOLDOWN = 30 * 1000;

  // 1. Performance: Use useCallback for handlers passed to children or used in forms
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (success) setSuccess(false);
    if (error) setError("");
  }, [success, error]);

  const onResumeUpload = useCallback((file) => {
    setForm(prev => ({ ...prev, resume: file }));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    const now = Date.now();
    if (now - lastSubmit.current < SUBMIT_COOLDOWN) {
      setError("Please wait a few seconds before submitting again.");
      return;
    }

    if (!form.resume) {
      setError("Please upload your resume.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      formData.append("job", job.title);

      const res = await fetch("/api/job-application", {
        method: "POST",
        body: formData
      });

      if (!res.ok) throw new Error("Submission failed");

      setSuccess(true);
      lastSubmit.current = Date.now();
      setResetKey(prev => prev + 1);
      setForm({ name: "", email: "", phone: "", linkedin: "", location: "", resume: null });
    } catch (err) {
      setError("Something went wrong. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  // 2. SEO: Job Posting Schema for Google Jobs
  const jsonLd = useMemo(() => {
    const fullDescription = [
      ...(job.description?.responsibilities || []),
      ...(job.description?.skills || []),
      ...(job.description?.experience || []),
      ...(job.description?.education || []),
    ].join(" ");
  
    const isRemote = String(job.location || "").toLowerCase() === "remote";
  
    const schema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: fullDescription,
      datePosted: job.datePosted,
  
      hiringOrganization: {
        "@type": "Organization",
        name: "Seternity Solutions",
        sameAs: "https://www.seternitysolutions.com",
        logo: "https://www.seternitysolutions.com/logo.png",
      },
  
      employmentType: job.employmentType || "FULL_TIME",
  
      identifier: {
        "@type": "PropertyValue",
        name: "Seternity Solutions",
        value: job.slug || String(job.id),
      },
    };
  
    if (isRemote) {
      schema.jobLocationType = "TELECOMMUTE";
      schema.applicantLocationRequirements = {
        "@type": "Country",
        name: job.applicantCountry || "IN",
      };
    } else {
      schema.jobLocation = {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          streetAddress: job.streetAddress || "Ground Floor, Mayur Van Area",
          addressLocality: job.city || job.location || "Udaipur",
          addressRegion: job.state || "Rajasthan",
          postalCode: job.postalCode || "313001",
          addressCountry: job.country || "IN",
        },
      };
    }
  
    if (job.validThrough) {
      schema.validThrough = job.validThrough;
    }
  
    if (job.salaryValue && job.salaryCurrency) {
      schema.baseSalary = {
        "@type": "MonetaryAmount",
        currency: job.salaryCurrency,
        value: {
          "@type": "QuantitativeValue",
          value: job.salaryValue,
          unitText: job.salaryUnit || "MONTH",
        },
      };
    }
  
    if (job.description?.education?.length) {
      schema.educationRequirements = job.description.education;
    }
  
    return schema;
  }, [job]);

  return (
    <section className="py-10 md:py-20 bg-white" aria-labelledby="job-title">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Link
          href="/career"
          className="text-blue-600 mb-6 md:mb-8 inline-flex items-center hover:underline text-sm md:text-base focus:ring-2 focus:ring-blue-500 rounded"
        >
          ← Back to roles
        </Link>

        <h1 id="job-title" className="text-3xl md:text-4xl font-bold mb-8 md:mb-10 text-slate-900">
          {job.title}
        </h1>

        {/* Accessibility: Tablist Role */}
        <div className="flex border-b mb-8 md:mb-12 gap-6 md:gap-10 overflow-x-auto" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "overview"}
            aria-controls="overview-panel"
            onClick={() => setTab("overview")}
            className={`cursor-pointer pb-3 font-semibold whitespace-nowrap transition-all outline-none focus-visible:ring-b-2 focus-visible:ring-blue-600 ${
              tab === "overview" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Overview
          </button>
          <button
            role="tab"
            aria-selected={tab === "application"}
            aria-controls="application-panel"
            onClick={() => setTab("application")}
            className={`cursor-pointer pb-3 font-semibold whitespace-nowrap transition-all outline-none focus-visible:ring-b-2 focus-visible:ring-blue-600 ${
              tab === "application" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Application
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          <aside className="space-y-8">
            <div>
              <p className="text-xs md:text-sm text-gray-500 mb-1 uppercase tracking-wider font-medium">Department</p>
              <p className="font-semibold text-lg text-slate-800">{job.department}</p>
              {tab !== "application" && (
                <button
                  type="button"
                  onClick={() => setTab("application")}
                  className="mt-4 inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-3 text-sm md:text-base font-semibold text-white shadow-sm transition hover:bg-blue-700 cursor-pointer"
                >
                  Apply Now
                </button>
              )}
            </div>
          </aside>

          <main className="md:col-span-2">
            {tab === "overview" && (
              <div id="overview-panel" role="tabpanel" className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                {[
                  { label: "Key Responsibilities", data: job.description.responsibilities },
                  { label: "Skills", data: job.description.skills },
                  { label: "Experience Required", data: job.description.experience },
                  { label: "Education Required", data: job.description.education },
                ].map((section) => (
                  <section key={section.label}>
                    <h3 className="font-bold text-slate-900 text-lg mb-3">{section.label}</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-700 leading-relaxed">
                      {section.data.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </section>
                ))}
              </div>
            )}

            {tab === "application" && (
              <div id="application-panel" role="tabpanel" className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                <h2 className="text-2xl font-bold mb-6 text-slate-900">Application Form</h2>

                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                  <div className="grid grid-cols-1 gap-4 md:gap-6">
                    {/* Accessibility: Labels are essential for screen readers */}
                    {[
                      { name: "name", placeholder: "Full Name", type: "text", required: true },
                      { name: "email", placeholder: "Email", type: "email", required: true },
                      { name: "phone", placeholder: "Phone Number", type: "tel", required: true },
                      { name: "linkedin", placeholder: "LinkedIn Profile (Optional)", type: "url", required: false },
                      { name: "location", placeholder: "Current Location", type: "text", required: true },
                    ].map((input) => (
                      <div key={input.name}>
                        <label htmlFor={input.name} className="sr-only">{input.placeholder}</label>
                        <input
                          id={input.name}
                          name={input.name}
                          type={input.type}
                          value={form[input.name]}
                          onChange={handleChange}
                          disabled={loading}
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none disabled:bg-gray-100 transition-all"
                          placeholder={input.placeholder}
                          required={input.required}
                          aria-required={input.required}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-2">
                    <ResumeUpload
                      key={resetKey}
                      onUpload={onResumeUpload}
                      disabled={loading}
                    />
                  </div>

                  {/* Accessibility: Live regions for errors/success */}
                  <div aria-live="polite">
                    {error && <p className="text-red-600 text-sm bg-red-50 border border-red-100 p-3 rounded-md mb-4">{error}</p>}
                    {success && <p className="text-green-700 font-medium bg-green-50 border border-green-100 p-3 rounded-md mb-4">Application submitted successfully! We'll be in touch soon.</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="cursor-pointer w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-3.5 rounded-lg transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    {loading ? "Submitting..." : "Submit Application"}
                  </button>
                </form>
              </div>
            )}
          </main>
        </div>
      </div>
    </section>
  );
}
